import * as React from 'react';
import { Combobox as ComboboxPrimitive } from '@base-ui/react';
import { cn } from 'utils/cn';
import { useOverlayScope } from 'components/ui/overlay-motion';

import { Button } from 'components/ui/button';
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from 'components/ui/input-group';
import { ChevronDownIcon, XIcon, CheckIcon } from 'lucide-react';

const SelectionAsPlaceholderContext = React.createContext(false);

function Combobox<Value, Multiple extends boolean | undefined = false>({
    selectionAsPlaceholder = false,
    onValueChange,
    ...props
}: ComboboxPrimitive.Root.Props<Value, Multiple> & {
    /** Single select only: opening the list shows the selected label as the input's placeholder. */
    selectionAsPlaceholder?: boolean;
}) {
    return (
        <SelectionAsPlaceholderContext.Provider value={selectionAsPlaceholder}>
            <ComboboxPrimitive.Root<Value, Multiple>
                {...props}
                onValueChange={(value, details) => {
                    // A deleted search is not a choice: Base UI would clear the selection with the input.
                    if (selectionAsPlaceholder && details.reason === 'input-clear') {
                        details.cancel();
                        return;
                    }
                    onValueChange?.(value, details);
                }}
            />
        </SelectionAsPlaceholderContext.Provider>
    );
}

function ComboboxValue({ ...props }: ComboboxPrimitive.Value.Props) {
    return <ComboboxPrimitive.Value data-slot="combobox-value" {...props} />;
}

function ComboboxTrigger({ className, children, ...props }: ComboboxPrimitive.Trigger.Props) {
    return (
        <ComboboxPrimitive.Trigger
            data-slot="combobox-trigger"
            aria-label="Show options"
            className={cn("cursor-pointer [&_svg:not([class*='size-'])]:size-4", className)}
            {...props}
        >
            {children}
            <ChevronDownIcon className="text-muted-foreground pointer-events-none size-4 stroke-[1.5px]!" />
        </ComboboxPrimitive.Trigger>
    );
}

function ComboboxClear({ className, ...props }: ComboboxPrimitive.Clear.Props) {
    return (
        <ComboboxPrimitive.Clear
            data-slot="combobox-clear"
            aria-label="Clear"
            render={<InputGroupButton variant="ghost" size="icon-xs" />}
            className={cn(className)}
            {...props}
        >
            <XIcon className="pointer-events-none stroke-[1.5px]!" />
        </ComboboxPrimitive.Clear>
    );
}

// A closed Base UI combobox takes Escape as "clear the value" and stops the key there. Skipping its handler
// while closed keeps the selection and the typed text, and lets Escape reach an enclosing dialog or drawer;
// an open popup still closes on Escape and consumes it. The caller's own handler runs first.
function keepValueOnEscape(
    onKeyDown: ComboboxPrimitive.Input.Props['onKeyDown']
): ComboboxPrimitive.Input.Props['onKeyDown'] {
    return (event) => {
        onKeyDown?.(event);
        if (event.key === 'Escape' && event.currentTarget.getAttribute('aria-expanded') !== 'true') {
            event.preventBaseUIHandler();
        }
    };
}

/** The text an edit put into `previous`; the caret ends right after it. */
function insertedText(previous: string, input: HTMLInputElement, edit: Partial<InputEvent>) {
    if (edit.inputType?.startsWith('delete')) {
        return '';
    }
    const next = input.value;
    const caret = input.selectionEnd ?? next.length;
    const insertedLength = edit.data?.length || edit.dataTransfer?.getData('text/plain').length;
    if (insertedLength) {
        return next.slice(caret - insertedLength, caret);
    }
    // Without the inserted text, the common prefix overreaches when the text matches what it replaced
    // ("A" typed over "Alpha"), so the length change caps it: an insertion adds at least one character.
    let start = 0;
    while (start < caret && previous[start] === next[start]) {
        start++;
    }
    return next.slice(Math.min(start, Math.max(0, caret - Math.max(1, next.length - previous.length))), caret);
}

// Base UI keeps the selected label as its input value while the list is open, which keeps the list
// unfiltered; only the rendered input is emptied. The list opens on `mousedown`, before the caret is placed,
// so the caret lands at the start of the empty input. The placeholder inherits the input's colour so the
// label fades with the input's colour transition.
function SelectionPlaceholderInput({
    open,
    clears,
    value,
    placeholder,
    className,
    onChange,
    ...props
}: React.ComponentProps<typeof InputGroupInput> & { open: boolean; clears: number }) {
    const [search, setSearch] = React.useState({
        listOpen: false,
        clears: 0,
        selectedLabel: '',
        typed: null as string | null,
    });
    const inputText = String(value ?? '');
    const isTyped = search.typed === inputText;
    if (search.listOpen !== open || search.clears !== clears) {
        const opens = open && !search.listOpen && search.clears === clears;
        setSearch({
            listOpen: open,
            clears,
            selectedLabel: opens ? (isTyped ? search.selectedLabel : inputText) : '',
            typed: opens ? search.typed : null,
        });
    } else if (open && !isTyped && inputText !== '' && inputText !== search.selectedLabel) {
        // Base UI rewrites its input to the label of a selection changed while the list is open.
        setSearch((current) => ({ ...current, selectedLabel: inputText }));
    }
    const showsPlaceholder = open && search.selectedLabel !== '' && (!isTyped || inputText === '');
    const showsSelection = !open && inputText !== '' && !isTyped;

    // A picked label shows no caret, and an edit anywhere in it starts a new search with what was put in.
    // Deleting from it does nothing: the clear button is how the field is emptied.
    const takeSearchText = (input: HTMLInputElement, edit: Partial<InputEvent>) => {
        if (!showsSelection) {
            setSearch((current) => ({ ...current, typed: input.value }));
            return true;
        }
        const text = insertedText(inputText, input, edit);
        if (text === '') {
            return false;
        }
        input.value = text;
        setSearch((current) => ({ ...current, typed: text, selectedLabel: inputText }));
        return true;
    };

    return (
        <InputGroupInput
            {...props}
            value={showsPlaceholder ? '' : value}
            placeholder={showsPlaceholder ? search.selectedLabel : placeholder}
            data-selection-placeholder={showsPlaceholder || undefined}
            className={cn(
                'new:ease-out duration-150',
                showsSelection ? 'caret-transparent' : 'caret-foreground new:caret-primary',
                (showsPlaceholder || inputText !== '') && 'placeholder:text-inherit',
                showsPlaceholder && 'text-muted-foreground',
                className
            )}
            onChange={(event) => {
                if (takeSearchText(event.currentTarget, event.nativeEvent as Partial<InputEvent>)) {
                    onChange?.(event);
                }
            }}
        />
    );
}

function ComboboxInput({
    className,
    children,
    disabled = false,
    showTrigger = true,
    showClear = false,
    onKeyDown,
    ...props
}: ComboboxPrimitive.Input.Props & {
    showTrigger?: boolean;
    showClear?: boolean;
}) {
    const selectionAsPlaceholder = React.use(SelectionAsPlaceholderContext);
    // The clear button keeps the list open and the focus in the input, so the input learns of it from here.
    const [clears, setClears] = React.useState(0);

    return (
        <InputGroup
            className={cn(
                'w-auto has-[>[data-align=inline-end]]:[&>input]:pr-8 has-[>[data-align=inline-end]>button]:[&>input]:pr-16',
                className
            )}
        >
            <ComboboxPrimitive.Input
                render={
                    selectionAsPlaceholder ? (
                        (inputProps, state) => (
                            <SelectionPlaceholderInput
                                {...inputProps}
                                open={state.open}
                                clears={clears}
                                disabled={disabled}
                            />
                        )
                    ) : (
                        <InputGroupInput disabled={disabled} />
                    )
                }
                onKeyDown={keepValueOnEscape(onKeyDown)}
                {...props}
            />
            {/* The clear button sits before the chevron and does not replace it, so a field with a value
                still shows it is a dropdown. Everything here but the clear button lies over the input and
                lets presses through to it, so a press beside the buttons or on the chevron is a press on
                the input: same cursor, caret and opening, no hover of its own. */}
            {(showClear || showTrigger) && (
                <InputGroupAddon
                    align="inline-end"
                    className={cn(
                        'pointer-events-none absolute inset-y-0 right-0 [&>button]:pointer-events-auto',
                        showTrigger && '-mr-1'
                    )}
                >
                    {showClear && (
                        <ComboboxClear
                            disabled={disabled}
                            onClick={selectionAsPlaceholder ? () => setClears((count) => count + 1) : undefined}
                        />
                    )}
                    {showTrigger && (
                        <span aria-hidden="true" className="flex size-6 items-center justify-center">
                            <ChevronDownIcon className="text-muted-foreground size-4 stroke-[1.5px]!" />
                        </span>
                    )}
                </InputGroupAddon>
            )}
            {children}
        </InputGroup>
    );
}

function ComboboxContent({
    className,
    side = 'bottom',
    sideOffset = 6,
    align = 'start',
    alignOffset = 0,
    anchor,
    positionerClassName,
    ...props
}: ComboboxPrimitive.Popup.Props &
    Pick<ComboboxPrimitive.Positioner.Props, 'side' | 'align' | 'sideOffset' | 'alignOffset' | 'anchor'> & {
        /** Classes for the portalled positioner — the place to raise `z-index` above app chrome. */
        positionerClassName?: string;
    }) {
    // In a page form the popup portals into the form body (`data-form-body`, set by `SectionLayout`): it
    // scrolls with the form, passes under the form's sticky header and is clipped by the page's scroll
    // area, so it never covers the environment banner above it. The create dialog sets the marker on its
    // own popup, which holds the list instead. Anywhere else, such as a popup, it portals to an AI
    // surface's container or to <body>. The form body is found from a marker in the DOM, not through
    // context, because a dialog opened from a form is still inside that form in React terms.
    // The popup is as wide as its longest option so a long name is not cut off, but capped so it never
    // outgrows the screen.
    const { container } = useOverlayScope();
    const formBody = React.useRef<HTMLElement | null>(null);
    const findFormBody = React.useCallback(
        (marker: HTMLSpanElement | null) => {
            formBody.current = marker?.closest<HTMLElement>('[data-form-body]') ?? container ?? null;
        },
        [container]
    );

    return (
        <>
            <span hidden ref={findFormBody} />
            <ComboboxPrimitive.Portal container={formBody}>
                <ComboboxPrimitive.Positioner
                    side={side}
                    sideOffset={sideOffset}
                    align={align}
                    alignOffset={alignOffset}
                    anchor={anchor}
                    className={cn('isolate z-(--z-dropdown)', positionerClassName)}
                >
                    <ComboboxPrimitive.Popup
                        data-slot="combobox-content"
                        data-chips={!!anchor}
                        className={cn(
                            'group/combobox-content bg-popover text-popover-foreground ring-foreground/10 data-[side=bottom]:slide-in-from-top-2 data-[side=inline-end]:slide-in-from-left-2 data-[side=inline-start]:slide-in-from-right-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 *:data-[slot=input-group]:border-input/30 *:data-[slot=input-group]:bg-input/30 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95 new:outline-hidden new:rounded-[12px] relative w-max max-w-[min(var(--available-width),max(var(--anchor-width),40rem))] min-w-[calc(var(--anchor-width)+--spacing(7))] origin-(--transform-origin) overflow-hidden rounded-md shadow-md ring-1 duration-100 data-[chips=true]:min-w-(--anchor-width) *:data-[slot=input-group]:m-1 *:data-[slot=input-group]:mb-0 *:data-[slot=input-group]:h-8 *:data-[slot=input-group]:shadow-none motion-reduce:animate-none!',
                            className
                        )}
                        {...props}
                    />
                </ComboboxPrimitive.Positioner>
            </ComboboxPrimitive.Portal>
        </>
    );
}

// A fixed 40vh, never clamped to the space left beside the field, so scrolling the page does not resize an
// open list; the positioner flips it to the other side instead.
function ComboboxList({ className, ...props }: ComboboxPrimitive.List.Props) {
    return (
        <ComboboxPrimitive.List
            data-slot="combobox-list"
            className={cn(
                'no-scrollbar new:scroll-py-1.5 new:p-1.5 new:data-empty:p-0 max-h-[40vh] scroll-py-1 overflow-y-auto overscroll-contain p-1 data-empty:p-0',
                className
            )}
            {...props}
        />
    );
}

function ComboboxItem({ className, children, ...props }: ComboboxPrimitive.Item.Props) {
    return (
        <ComboboxPrimitive.Item
            data-slot="combobox-item"
            className={cn(
                "data-highlighted:bg-accent data-highlighted:text-accent-foreground not-data-[variant=destructive]:data-highlighted:**:text-accent-foreground relative flex w-full cursor-default items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm outline-hidden select-none data-disabled:pointer-events-none data-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
                className
            )}
            {...props}
        >
            {children}
            <ComboboxPrimitive.ItemIndicator
                render={
                    <span className="pointer-events-none absolute right-2 flex size-4 items-center justify-center" />
                }
            >
                <CheckIcon className="pointer-events-none" />
            </ComboboxPrimitive.ItemIndicator>
        </ComboboxPrimitive.Item>
    );
}

function ComboboxGroup({ className, ...props }: ComboboxPrimitive.Group.Props) {
    return <ComboboxPrimitive.Group data-slot="combobox-group" className={cn(className)} {...props} />;
}

function ComboboxLabel({ className, ...props }: ComboboxPrimitive.GroupLabel.Props) {
    return (
        <ComboboxPrimitive.GroupLabel
            data-slot="combobox-label"
            className={cn('text-muted-foreground px-2 py-1.5 text-xs', className)}
            {...props}
        />
    );
}

function ComboboxCollection({ ...props }: ComboboxPrimitive.Collection.Props) {
    return <ComboboxPrimitive.Collection data-slot="combobox-collection" {...props} />;
}

function ComboboxEmpty({ className, ...props }: ComboboxPrimitive.Empty.Props) {
    return (
        <ComboboxPrimitive.Empty
            data-slot="combobox-empty"
            className={cn(
                'text-muted-foreground hidden w-full justify-center py-2 text-center text-sm group-data-empty/combobox-content:flex',
                className
            )}
            {...props}
        />
    );
}

function ComboboxSeparator({ className, ...props }: ComboboxPrimitive.Separator.Props) {
    return (
        <ComboboxPrimitive.Separator
            data-slot="combobox-separator"
            className={cn('bg-border -mx-1 my-1 h-px', className)}
            {...props}
        />
    );
}

function ComboboxChips({
    className,
    ...props
}: React.ComponentPropsWithRef<typeof ComboboxPrimitive.Chips> & ComboboxPrimitive.Chips.Props) {
    return (
        <ComboboxPrimitive.Chips
            data-slot="combobox-chips"
            className={cn(
                'border-input focus-within:border-ring focus-within:ring-ring/50 has-aria-invalid:border-destructive has-aria-invalid:ring-destructive/20 dark:bg-input/30 dark:has-aria-invalid:border-destructive/50 dark:has-aria-invalid:ring-destructive/40 flex min-h-9 flex-wrap items-center gap-1.5 rounded-md border bg-transparent bg-clip-padding px-2.5 py-1.5 text-base/[1.25] shadow-xs transition-[color,box-shadow] focus-within:ring-3 has-aria-invalid:ring-3 has-data-[slot=combobox-chip]:px-1.5 md:text-sm',
                className
            )}
            {...props}
        />
    );
}

function ComboboxChip({
    className,
    children,
    showRemove = true,
    ...props
}: ComboboxPrimitive.Chip.Props & {
    showRemove?: boolean;
}) {
    return (
        <ComboboxPrimitive.Chip
            data-slot="combobox-chip"
            className={cn(
                'bg-muted text-foreground flex h-[calc(--spacing(5.5))] w-fit items-center justify-center gap-1 rounded-sm px-1.5 text-xs font-medium whitespace-nowrap has-disabled:pointer-events-none has-disabled:cursor-not-allowed has-disabled:opacity-50 has-data-[slot=combobox-chip-remove]:pr-0',
                className
            )}
            {...props}
        >
            {children}
            {showRemove && (
                <ComboboxPrimitive.ChipRemove
                    render={<Button variant="ghost" size="icon-xs" />}
                    className="-ml-1 opacity-50 hover:opacity-100"
                    data-slot="combobox-chip-remove"
                >
                    <XIcon className="pointer-events-none" />
                </ComboboxPrimitive.ChipRemove>
            )}
        </ComboboxPrimitive.Chip>
    );
}

function ComboboxChipsInput({ className, onKeyDown, ...props }: ComboboxPrimitive.Input.Props) {
    return (
        <ComboboxPrimitive.Input
            data-slot="combobox-chip-input"
            className={cn('placeholder:text-muted-foreground min-w-16 flex-1 outline-none', className)}
            onKeyDown={keepValueOnEscape(onKeyDown)}
            {...props}
        />
    );
}

function useComboboxAnchor() {
    return React.useRef<HTMLDivElement | null>(null);
}

export {
    Combobox,
    ComboboxInput,
    ComboboxContent,
    ComboboxList,
    ComboboxItem,
    ComboboxGroup,
    ComboboxLabel,
    ComboboxCollection,
    ComboboxEmpty,
    ComboboxSeparator,
    ComboboxChips,
    ComboboxChip,
    ComboboxChipsInput,
    ComboboxClear,
    ComboboxTrigger,
    ComboboxValue,
    useComboboxAnchor,
};
